import type { Appointment } from '../data/types';

/** Parse YYYY-MM-DD as local midnight (matches Medical Record timeline). */
export function parseDateLocal(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(value);
}

/**
 * Local appointment start (or end-of-day if time missing) for comparing to "now".
 * Times like "4:00 PM" are supported.
 */
function getAppointmentStartLocal(apt: Pick<Appointment, 'date' | 'time'>): Date {
  const d = parseDateLocal(apt.date);
  const t = apt.time?.trim();
  if (!t) {
    d.setHours(9, 0, 0, 0);
    return d;
  }
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(t);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toUpperCase();
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    d.setHours(h, min, 0, 0);
    return d;
  }
  const fallback = new Date(`${apt.date} ${t}`);
  if (!Number.isNaN(fallback.getTime())) return fallback;
  d.setHours(9, 0, 0, 0);
  return d;
}

/** Grace period after scheduled end before "Pending Documentation" / tasks (matches FHIR helper). */
export const APPOINTMENT_POST_SESSION_DELAY_MS = 5 * 60 * 1000;

type ChartAppointmentWindow = Pick<
  Appointment,
  'date' | 'time' | 'endIso' | 'minutesDuration'
>;

/** Scheduled end (ms) from Medplum `end` or `start` + `minutesDuration`. */
export function getChartAppointmentEndMs(apt: ChartAppointmentWindow): number | undefined {
  if (apt.endIso) {
    const end = new Date(apt.endIso).getTime();
    if (!Number.isNaN(end)) return end;
  }
  const startMs = getAppointmentStartLocal(apt).getTime();
  if (apt.minutesDuration != null && apt.minutesDuration > 0) {
    return startMs + apt.minutesDuration * 60_000;
  }
  return undefined;
}

/**
 * True when the scheduled session window has ended (past `end` + short delay), so documentation may be due.
 */
/** Scheduled start is still in the future (includes later today). */
export function isFutureAppointment(
  apt: Pick<Appointment, 'date' | 'time'>,
  nowMs = Date.now(),
): boolean {
  return getAppointmentStartLocal(apt).getTime() > nowMs;
}

export function isAppointmentPostSession(
  apt: ChartAppointmentWindow,
  nowMs = Date.now(),
): boolean {
  const endMs = getChartAppointmentEndMs(apt);
  if (endMs != null) {
    return nowMs > endMs + APPOINTMENT_POST_SESSION_DELAY_MS;
  }
  if (!apt.time?.trim()) {
    const dayEnd = parseDateLocal(apt.date);
    dayEnd.setHours(23, 59, 59, 999);
    return nowMs > dayEnd.getTime() + APPOINTMENT_POST_SESSION_DELAY_MS;
  }
  return false;
}

export const PENDING_DOCUMENTATION_LABEL = 'Pending Documentation';
